import React, { useEffect, useState } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';

/**
 * Admin-only panel (ImpactGolf master catalog admin / superadmin) to toggle a
 * manufacturer's platform-wide "Call for Pricing" restriction. When on, the
 * brand's products stay browsable everywhere (including reseller storefronts
 * on the fulfillment catalog) but show "Call for Pricing" instead of a price
 * and cannot be added to cart.
 */
export default function BrandAvailabilityModal({ open, onClose }) {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingBrand, setSavingBrand] = useState(null);

  useEffect(() => {
    if (open) loadBrands();
  }, [open]);

  const loadBrands = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/brand-restrictions');
      setBrands(response.data);
    } catch (error) {
      toast.error('Failed to load brand availability');
    } finally {
      setLoading(false);
    }
  };

  const toggleBrand = async (manufacturer, nextValue) => {
    setSavingBrand(manufacturer);
    try {
      await api.put(`/admin/brand-restrictions/${encodeURIComponent(manufacturer)}`, {
        is_call_for_pricing: nextValue,
      });
      setBrands(prev => prev.map(b => b.manufacturer === manufacturer ? { ...b, is_call_for_pricing: nextValue } : b));
      toast.success(`${manufacturer} ${nextValue ? 'set to Call for Pricing' : 'available for purchase again'}`);
    } catch (error) {
      toast.error(`Failed to update ${manufacturer}`);
    } finally {
      setSavingBrand(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="brand-availability-modal">
        <DialogHeader>
          <DialogTitle>Brand Availability</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-500 -mt-2 mb-2">
          Turn "Call for Pricing" on for a brand to hide its price and block Add to Cart
          platform-wide (including reseller storefronts), while keeping products browsable.
        </p>
        {loading ? (
          <p className="text-sm text-gray-500 py-6 text-center">Loading brands...</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {brands.map(b => (
              <li key={b.manufacturer} className="flex items-center justify-between py-3" data-testid={`brand-row-${b.manufacturer}`}>
                <span className="text-sm font-medium text-gray-900">{b.manufacturer}</span>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold ${b.is_call_for_pricing ? 'text-amber-700' : 'text-gray-400'}`}>
                    {b.is_call_for_pricing ? 'Call for Pricing' : 'Available'}
                  </span>
                  <Switch
                    checked={b.is_call_for_pricing}
                    disabled={savingBrand === b.manufacturer}
                    onCheckedChange={(v) => toggleBrand(b.manufacturer, v)}
                    data-testid={`brand-toggle-${b.manufacturer}`}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
